import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, AlertCircle, Clock, User, MapPin, Phone, FileText, Activity, CircleUser as UserCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/Button';
import { Header } from '../components/Header';
import { Input } from '../components/Input';
import { BookingCard } from '../components/BookingCard';
import { BookingStatusBadge } from '../components/BookingStatusBadge';
import { Modal } from '../components/Modal';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { acceptBooking, rejectBooking, completeBooking, getBookingDetails, getBookingHistory } from '../services/api';
import { Booking, BookingStatus } from '../types';
import { ROUTES } from '../constants';

export const ProviderBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bookingId } = useParams<{ bookingId: string }>();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [rejectingBookingId, setRejectingBookingId] = useState<number | null>(null);
  const [completingBookingId, setCompletingBookingId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<BookingStatus | 'All'>('All');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchBookings = async (page: number = currentPage) => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await getBookingHistory({ page, limit: 10 });
      if (response.success) {
        setBookings(response.data || []);
        setTotalPages(response.pagination.totalPages);
        setTotalCount(response.pagination.totalCount);
      } else {
        setErrorMessage(response.error || 'Failed to fetch bookings');
      }
    } catch (err) {
      setErrorMessage('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (bookingId && bookings.length > 0 && !showDetailsModal) {
      const bookingIdNum = parseInt(bookingId, 10);
      if (!isNaN(bookingIdNum)) {
        handleViewDetails(bookingIdNum);
      }
    }
  }, [bookingId, bookings]);

  const handleViewDetails = async (bookingId: number) => {
    const localBooking = bookings.find(b => b.bookingId === bookingId);
    if (localBooking) {
      setSelectedBooking(localBooking);
      setShowDetailsModal(true);
    }
    setActionLoading(true);
    try {
      const response = await getBookingDetails(bookingId);
      if (response.success && response.booking) {
        const merged = {
          ...localBooking,
          ...response.booking,
          bookingId: response.booking.bookingId ?? localBooking?.bookingId,
          serviceType: response.booking.serviceType || localBooking?.serviceType,
          appointmentDate: response.booking.appointmentDate || localBooking?.appointmentDate,
          status: response.booking.status || localBooking?.status,
        };
        setSelectedBooking(merged as Booking);
        setShowDetailsModal(true);
      }
    } catch (err) {
      console.error('Failed to fetch booking details:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccept = async (bookingId: number) => {
    setActionLoading(true);
    setErrorMessage(null);
    try {
      const response = await acceptBooking(bookingId);
      if (response.success) {
        setSuccessMessage('Booking approved successfully');
        await fetchBookings(currentPage);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setErrorMessage(response.message || 'Failed to approve booking');
      }
    } catch (err) {
      setErrorMessage('Failed to approve booking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (bookingId: number) => {
    setRejectingBookingId(bookingId);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectingBookingId) return;

    if (!rejectionReason.trim()) {
      setErrorMessage('Please provide a reason for not approving');
      return;
    }

    setActionLoading(true);
    setErrorMessage(null);
    try {
      const response = await rejectBooking(rejectingBookingId, rejectionReason);
      if (response.success) {
        setSuccessMessage('Booking not approved successfully');
        setShowRejectModal(false);
        setRejectingBookingId(null);
        setRejectionReason('');
        await fetchBookings(currentPage);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setErrorMessage(response.message || 'Failed to not approve booking');
      }
    } catch (err) {
      setErrorMessage('Failed to not approve booking');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteClick = (bookingId: number) => {
    setCompletingBookingId(bookingId);
    setShowCompleteModal(true);
  };

  const handleCompleteConfirm = async () => {
    if (!completingBookingId) return;

    setActionLoading(true);
    setErrorMessage(null);
    try {
      const response = await completeBooking(completingBookingId);
      if (response.success) {
        setSuccessMessage('Booking marked as completed');
        setShowCompleteModal(false);
        setCompletingBookingId(null);
        await fetchBookings(currentPage);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setErrorMessage(response.message || 'Failed to complete booking');
      }
    } catch (err) {
      setErrorMessage('Failed to complete booking');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredBookings = filterStatus === 'All'
    ? bookings
    : bookings.filter(b => b.status === filterStatus);

  const getBookingCounts = () => {
    return {
      all: totalCount,
      pending: bookings.filter(b => b.status === 'Pending' || b.status?.toLowerCase() === 'pending').length,
      accepted: bookings.filter(b => b.status === 'Accepted' || b.status?.toLowerCase() === 'accepted').length,
      completed: bookings.filter(b => b.status === 'Completed' || b.status?.toLowerCase() === 'completed').length,
      cancelled: bookings.filter(b => b.status === 'Cancelled' || b.status?.toLowerCase() === 'cancelled').length,
      rejected: bookings.filter(b => b.status === 'Rejected' || b.status?.toLowerCase() === 'rejected').length,
    };
  };

  const counts = getBookingCounts();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      <Header
        theme="green"
        showNotifications={true}
        notificationCount={counts.pending}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(ROUTES.HOME_PAGE)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Service Requests</h1>
          <p className="text-gray-600">Manage your booking requests and appointments</p>
        </div>

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilterStatus('All')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'All'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            All ({counts.all})
          </button>
          <button
            onClick={() => setFilterStatus('Pending')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Pending ({counts.pending})
          </button>
          <button
            onClick={() => setFilterStatus('Accepted')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Accepted'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Approved ({counts.accepted})
          </button>
          <button
            onClick={() => setFilterStatus('Completed')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Completed'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Completed ({counts.completed})
          </button>
          <button
            onClick={() => setFilterStatus('Cancelled')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Cancelled'
                ? 'bg-gray-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Cancelled ({counts.cancelled})
          </button>
          <button
            onClick={() => setFilterStatus('Rejected')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filterStatus === 'Rejected'
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Not Approved ({counts.rejected})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {filterStatus === 'All' ? 'No booking requests yet' : `No ${filterStatus.toLowerCase()} bookings`}
            </h3>
            <p className="text-gray-600">
              {filterStatus === 'All'
                ? 'When clients book your services, they will appear here'
                : `You don't have any ${filterStatus.toLowerCase()} bookings at the moment`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <BookingCard
                key={booking.bookingId}
                booking={booking}
                userRole="provider"
                onViewDetails={handleViewDetails}
                onAccept={handleAccept}
                onReject={handleRejectClick}
                onComplete={handleCompleteClick}
                userName={booking.patientSnapshot?.recipientName || `User #${booking.userId}`}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && !loading && (
          <div className="flex items-center justify-between mt-8 bg-white rounded-xl border border-gray-200 px-6 py-4">
            <p className="text-sm text-gray-600">
              Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
              <span className="font-semibold text-gray-900">{totalPages}</span>
              <span className="ml-2 text-gray-400">({totalCount} total)</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </main>

      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Booking Details"
      >
        {selectedBooking && (
          <div className="space-y-6">
            <div className={`rounded-xl p-6 ${
              selectedBooking.status === 'Accepted' ? 'bg-blue-50 border-2 border-blue-200' :
              selectedBooking.status === 'Pending' ? 'bg-yellow-50 border-2 border-yellow-200' :
              selectedBooking.status === 'Rejected' ? 'bg-red-50 border-2 border-red-200' :
              selectedBooking.status === 'Completed' ? 'bg-green-50 border-2 border-green-200' :
              'bg-gray-50 border-2 border-gray-200'
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Booking ID</p>
                  <p className="text-2xl font-bold text-gray-900">#{selectedBooking.bookingId}</p>
                </div>
                <BookingStatusBadge status={selectedBooking.status} />
              </div>

              <div className="grid grid-cols-1 gap-4 pt-4 border-t border-gray-300">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedBooking.status === 'Accepted' ? 'bg-blue-100' :
                    selectedBooking.status === 'Pending' ? 'bg-yellow-100' :
                    selectedBooking.status === 'Rejected' ? 'bg-red-100' :
                    selectedBooking.status === 'Completed' ? 'bg-green-100' :
                    'bg-gray-100'
                  }`}>
                    <Activity className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Service Type</p>
                    <p className="font-semibold text-gray-900">{selectedBooking.serviceType}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedBooking.status === 'Accepted' ? 'bg-blue-100' :
                    selectedBooking.status === 'Pending' ? 'bg-yellow-100' :
                    selectedBooking.status === 'Rejected' ? 'bg-red-100' :
                    selectedBooking.status === 'Completed' ? 'bg-green-100' :
                    'bg-gray-100'
                  }`}>
                    <Calendar className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Appointment Date</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(selectedBooking.appointmentDate).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    selectedBooking.status === 'Accepted' ? 'bg-blue-100' :
                    selectedBooking.status === 'Pending' ? 'bg-yellow-100' :
                    selectedBooking.status === 'Rejected' ? 'bg-red-100' :
                    selectedBooking.status === 'Completed' ? 'bg-green-100' :
                    'bg-gray-100'
                  }`}>
                    <Clock className="w-5 h-5 text-gray-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(selectedBooking.appointmentDate).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {selectedBooking.userDetails && (
              <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl p-6 border border-sky-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <UserCircle className="w-6 h-6 text-sky-700" />
                  <h3 className="text-lg font-bold text-gray-900">Booked By</h3>
                </div>

                <div className="bg-white rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Name</p>
                      <p className="font-semibold text-gray-900">{selectedBooking.userDetails.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Email</p>
                      <p className="font-semibold text-gray-900 break-all">{selectedBooking.userDetails.email}</p>
                    </div>
                    {selectedBooking.userDetails.city && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">City</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.userDetails.city}</p>
                      </div>
                    )}
                    {selectedBooking.userDetails.preferredCaregiverGender && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Preferred Caregiver Gender</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.userDetails.preferredCaregiverGender}</p>
                      </div>
                    )}
                  </div>

                  {(selectedBooking.userDetails.careShift || selectedBooking.userDetails.careFrequency) && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {selectedBooking.userDetails.careShift && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Care Shift Preference</p>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800 border border-sky-200">
                              {selectedBooking.userDetails.careShift}
                            </span>
                          </div>
                        )}
                        {selectedBooking.userDetails.careFrequency && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Care Frequency</p>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-sky-100 text-sky-800 border border-sky-200">
                              {selectedBooking.userDetails.careFrequency}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedBooking.patientSnapshot && (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-6 h-6 text-blue-700" />
                  <h3 className="text-lg font-bold text-gray-900">Patient Information</h3>
                </div>

                <div className="bg-white rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Full Name</p>
                      <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.recipientName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Age</p>
                      <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.recipientAge} years</p>
                    </div>
                    {selectedBooking.patientSnapshot.recipientGender && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Gender</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.recipientGender}</p>
                      </div>
                    )}
                    {selectedBooking.patientSnapshot.mobilityStatus && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Mobility Status</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.mobilityStatus}</p>
                      </div>
                    )}
                  </div>

                  {selectedBooking.patientSnapshot.medicalConditions && selectedBooking.patientSnapshot.medicalConditions.length > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Medical Conditions</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedBooking.patientSnapshot.medicalConditions.map((condition, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                            {condition}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedBooking.patientSnapshot.equipmentDetails && (
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">Equipment Required</p>
                      <p className="text-sm text-gray-900 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                        {selectedBooking.patientSnapshot.equipmentDetails}
                      </p>
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex items-start gap-2 mb-3">
                      <MapPin className="w-4 h-4 text-gray-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Service Address</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedBooking.patientSnapshot.address}
                        </p>
                        <p className="text-sm text-gray-600">{selectedBooking.patientSnapshot.city}</p>
                      </div>
                    </div>
                  </div>

                  <div className=" bg-red-50 rounded-lg p-3 border border-red-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="w-4 h-4 text-red-700" />
                      <p className="text-xs font-semibold text-red-700 uppercase">Emergency Contact</p>
                    </div>
                    <div>
                      <p className="text-xs text-red-600 mb-1">Name</p>
                      <p className="font-semibold text-gray-900">{selectedBooking.patientSnapshot.emergencyContactName}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedBooking.notes && (
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold text-gray-900">Additional Notes</h3>
                </div>
                <p className="text-gray-700 leading-relaxed bg-white rounded-lg p-4 border border-gray-200">
                  {selectedBooking.notes}
                </p>
              </div>
            )}

            {selectedBooking.rejectionReason && (
              <div className="bg-red-50 rounded-xl p-5 border-2 border-red-300">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-red-700" />
                  <h3 className="font-semibold text-red-900">Reason for Not Approving</h3>
                </div>
                <p className="text-red-800 leading-relaxed bg-white rounded-lg p-4 border border-red-200">
                  {selectedBooking.rejectionReason}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Not Approve Booking"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Please provide a reason for not approving this booking request:
          </p>
          <Input
            label="Reason for Not Approving"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="e.g., Unavailable on this date"
            required
          />
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowRejectModal(false)}
              className="flex-1"
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRejectConfirm}
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={actionLoading || !rejectionReason.trim()}
            >
              {actionLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" />
                  Not Approving...
                </span>
              ) : (
                'Confirm Not Approve'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        title="Complete Booking"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to mark this booking as completed? This confirms that the service has been provided.
          </p>
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowCompleteModal(false)}
              className="flex-1"
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCompleteConfirm}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" />
                  Completing...
                </span>
              ) : (
                'Mark as Completed'
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
